import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Option {
  id: string;
  text: string;
}

export interface Topic {
  title: string;
  content: string;
}

export interface Question {
  id: string;
  text: string;
  options: Option[];
  correctOptionId: string;
  explanation?: string;
  topic?: Topic | null;
}

export const getQuestionCount = async (
  subjectId?: string,
  categoryId?: string | null
): Promise<number> => {
  try {
    let query = supabase
      .from('questions')
      .select('id', { count: 'exact' });
    
    if (subjectId) {
      query = query.eq('subject_id', subjectId);
    }
    
    if (categoryId && categoryId !== 'undefined' && categoryId !== 'null') {
      query = query.eq('category_id', categoryId);
    }
    
    const { count, error } = await query;
    
    if (error) {
      console.error("Error counting questions:", error);
      return 0;
    }
    
    return count || 0;
  } catch (error) {
    console.error("Error in getQuestionCount:", error);
    return 0;
  }
};

export const loadQuestionsFromDB = async (
  subject: string,
  grade: string,
  questionCount: number,
  categoryId?: string | null
): Promise<Question[]> => {
  console.log(`Loading ${questionCount} questions from database for ${subject} grade ${grade}, category ID: ${categoryId || 'none'}`);
  
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    // Construct the database query
    let query = supabase
      .from('questions')
      .select(`
        id,
        text,
        explanation,
        topic:topic_id (
          title,
          content
        ),
        options (
          id,
          text,
          is_correct
        )
      `)
      .eq('subject_id', subject)
      .order('created_at', { ascending: false });
    
    // Add category filter if provided
    if (categoryId && categoryId !== 'undefined' && categoryId !== 'null') {
      query = query.eq('category_id', categoryId);
    }
    
    // Execute the query to get all available questions
    const { data: allQuestions, error } = await query;
    
    if (error) {
      console.error("Error fetching questions from database:", error);
      toast.error("Error retrieving questions");
      return [];
    }
    
    if (!allQuestions || allQuestions.length === 0) {
      console.log("No questions found in database");
      return [];
    }
    
    console.log("Raw questions from database:", allQuestions);
    
    // Find previously answered questions by the current user
    let previouslyAnsweredQuestionIds: string[] = [];
    
    if (user) {
      try {
        // Get test sessions for this subject and user
        const { data: testSessions } = await supabase
          .from('test_sessions')
          .select('id')
          .eq('subject_id', subject)
          .eq('user_id', user.id)
          .eq('completed', true);
          
        if (testSessions && testSessions.length > 0) {
          // Extract session IDs
          const sessionIds = testSessions.map(session => session.id);
          
          // Get question IDs from these sessions
          const { data: answers } = await supabase
            .from('test_answers')
            .select('question_id')
            .in('test_session_id', sessionIds);
            
          if (answers && answers.length > 0) {
            // Extract unique question IDs
            previouslyAnsweredQuestionIds = [...new Set(answers.map(answer => answer.question_id))];
            console.log(`Found ${previouslyAnsweredQuestionIds.length} previously answered questions by the user`);
          }
        }
      } catch (error) {
        console.error("Error fetching previously answered questions:", error);
      }
    }
    
    // Filter out previously answered questions if possible
    let availableQuestions = allQuestions;
    
    if (previouslyAnsweredQuestionIds.length > 0) {
      const newQuestions = allQuestions.filter(q => !previouslyAnsweredQuestionIds.includes(q.id));
      
      if (newQuestions.length >= questionCount) {
        console.log(`Using ${newQuestions.length} new questions that the user hasn't seen before`);
        availableQuestions = newQuestions;
      } else {
        console.log(`Not enough new questions (only ${newQuestions.length}), using all available questions`);
        // We keep all questions if there aren't enough new ones
      }
    }
    
    console.log(`Successfully loaded ${availableQuestions.length} questions from database`);
    
    // Transform the questions from the database format to the app format
    const formattedQuestions: Question[] = availableQuestions.map((q) => {
      // Find the correct option
      const correctOption = q.options.find((o) => o.is_correct);
      
      // Map options to the expected format
      const options = q.options.map((o) => ({
        id: o.id,
        text: o.text
      }));
      
      return {
        id: q.id,
        text: q.text,
        options,
        correctOptionId: correctOption ? correctOption.id : '',
        explanation: q.explanation,
        topic: q.topic || null
      };
    });
    
    // Log some debug information about the questions and topics
    console.log(`Topic-based questions: ${formattedQuestions.filter(q => q.topic).length}`);
    console.log(`Non-topic questions: ${formattedQuestions.filter(q => !q.topic).length}`);
    
    // Shuffle the questions to get different ones each time
    const shuffledQuestions = [...formattedQuestions]
      .sort(() => Math.random() - 0.5)
      .slice(0, questionCount);
    
    console.log(`Returning ${shuffledQuestions.length} questions after shuffling and limiting`);
    return shuffledQuestions;
  } catch (error) {
    console.error("Error in loadQuestionsFromDB:", error);
    toast.error("Failed to load questions");
    return [];
  }
};

export const generateQuestions = async (
  subject: string, 
  grade: string, 
  questionCount: number,
  categoryId?: string | null
): Promise<Question[]> => {
  console.log(`Generating ${questionCount} questions for ${subject} grade ${grade}, category ID: ${categoryId || 'none'}`);
  
  try {
    // If subject is a UUID, fetch the subject name
    let subjectName = subject;
    let categoryName = null;
    
    if (subject && subject.includes('-')) {
      // It's a UUID, fetch the subject name
      const { data: subjectData, error: subjectError } = await supabase
        .from('subjects')
        .select('name')
        .eq('id', subject)
        .maybeSingle();
        
      if (subjectError) {
        console.error("Error fetching subject name:", subjectError);
        toast.error("Error fetching subject information");
        return [];
      }
      
      if (subjectData) {
        subjectName = subjectData.name;
      }
    }
    
    // If categoryId is provided and not undefined/null, fetch the category name
    if (categoryId && categoryId !== 'undefined' && categoryId !== 'null') {
      const { data: categoryData, error: categoryError } = await supabase
        .from('subjects_categories')
        .select('name')
        .eq('id', categoryId)
        .maybeSingle();
        
      if (categoryError) {
        console.error("Error fetching category name:", categoryError);
      } else if (categoryData) {
        categoryName = categoryData.name;
      }
    }
    
    // Find previously answered questions to avoid duplicates
    let answeredQuestionIds: string[] = [];
    
    if (subject) {
      try {
        // Get test sessions for this subject
        const { data: testSessions } = await supabase
          .from('test_sessions')
          .select('id')
          .eq('subject_id', subject)
          .eq('completed', true);
          
        if (testSessions && testSessions.length > 0) {
          // Extract session IDs
          const sessionIds = testSessions.map(session => session.id);
          
          // Get question IDs from these sessions
          const { data: answers } = await supabase
            .from('test_answers')
            .select('question_id')
            .in('test_session_id', sessionIds);
            
          if (answers && answers.length > 0) {
            // Extract unique question IDs
            answeredQuestionIds = [...new Set(answers.map(answer => answer.question_id))];
            console.log(`Found ${answeredQuestionIds.length} previously answered questions`);
          }
        }
      } catch (error) {
        console.error("Error fetching previously answered questions:", error);
      }
    }
    
    console.log(`Sending request for ${questionCount} questions to generate-questions function`);

    // Call Supabase Edge Function to generate questions
    const { data, error } = await supabase.functions.invoke('generate-questions', {
      body: {
        subject_id: subject,
        grade,
        count: questionCount, // Pass the exact count needed
        category_id: categoryId,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        randomSeed: Date.now() // Add some randomness
      }
    });
    
    if (error) {
      console.error("Error calling generate-questions function:", error);
      toast.error("Error generating questions");
      return [];
    }
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error("No questions returned from function");
      toast.error("No questions available");
      return [];
    }
    
    // Log what we received from the function
    console.log(`Successfully generated ${data.length} questions`);
    
    // Transform the questions from the database format to the app format
    const questions: Question[] = data.map((q: any) => {
      // Find the correct option
      const correctOption = q.options.find((o: any) => o.is_correct);
      
      // Map options to the expected format
      const options = q.options.map((o: any) => ({
        id: o.id,
        text: o.text
      }));
      
      return {
        id: q.id,
        text: q.text,
        options,
        correctOptionId: correctOption ? correctOption.id : '',
        explanation: q.explanation,
        topic: q.topic
      };
    });
    
    // Ensure we're limiting to the requested count (edge function should already do this,
    // but this adds an extra layer of certainty)
    const limitedQuestions = questions.slice(0, questionCount);
    console.log(`Returning ${limitedQuestions.length} questions after limiting to requested count`);
    
    return limitedQuestions;
  } catch (error) {
    console.error("Error in generateQuestions:", error);
    toast.error("Failed to generate questions");
    return [];
  }
};
